import React from 'react'

import { ContractTaxonomyFragment } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import type { Params } from 'modules/core/components/Inputs/AutoSaveInput'
import Select from 'modules/core/components/Inputs/Select'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import CompensationTypePopoverMenu from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/CompensationTypePopoverMenu'
import ControlInput from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/ControlInput'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'

interface TaxonomyDropdownProps {
  form: SupplementForm
  isReadOnly: boolean
  taxonomies: ContractTaxonomyFragment[]
  canBulkCreateContractsTaxonomies: boolean
  onSubmit: () => Promise<unknown>
  compensationsTypeSize?: 'expansive' | 'compact'
  toggleTaxonomyCreationModal: (newState: boolean) => void
  hasError?: boolean
}

const TaxonomyDropdown = ({
  form,
  taxonomies,
  isReadOnly,
  canBulkCreateContractsTaxonomies,
  onSubmit,
  compensationsTypeSize = 'compact',
  toggleTaxonomyCreationModal,
  hasError = false,
}: TaxonomyDropdownProps) => {
  const taxonomiesByLegalEntity = groupBy(
    taxonomies.filter((taxonomy) => !taxonomy.archived),
    (taxonomy) => taxonomy.legalEntityId
  )
  const legalEntity = form.employee && employeeLegalEntity(form.employee)
  const options =
    taxonomiesByLegalEntity[legalEntity?.id]
      ?.map((option: ContractTaxonomyFragment) => ({
        label: option.name,
        value: option.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  return canBulkCreateContractsTaxonomies ? (
    <CompensationTypePopoverMenu
      hasError={hasError}
      form={form}
      options={options}
      onSubmit={onSubmit}
      readonly={isReadOnly}
      size={compensationsTypeSize}
      toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
    />
  ) : (
    <ControlInput onSubmit={onSubmit} isSelect>
      {(params: Params) => (
        <Select
          {...params}
          none={i18n.t('common.select')}
          field={form.get('contracts_taxonomy_id')}
          readonly={isReadOnly}
          options={options}
          mandatory
        />
      )}
    </ControlInput>
  )
}

export default TaxonomyDropdown
